/**
 * Pure scan function used by both BookingPanel and RescheduleDialog to
 * suggest 2-3 nearest valid slots when a client picks an unavailable time.
 *
 * Keeps the availability math in one place, delegates the per-day slot
 * computation back to the caller so we don't duplicate the
 * weekly_schedule / blocked_dates / booked_slots / min_notice logic.
 *
 *  computeSlotsForDate(date)  -> Array<{ time: 'HH:MM', status: 'available'|'booked' }>
 *  isDateDisabled(date)       -> bool  (closed days, min-notice, full-all-day blocks)
 *
 * Returns at most `count` entries. Same-day suggestions come first (strictly
 * AFTER fromTime, so we never loop the client back to the slot they just
 * found was taken), then scan forward up to `lookAheadDays`.
 */
export function getNextAvailableSlots({
  availability,
  computeSlotsForDate,
  isDateDisabled,
  fromDate,
  fromTime,
  count = 3,
  lookAheadDays = 7,
}) {
  if (!availability || !fromDate || typeof computeSlotsForDate !== 'function') {
    return [];
  }

  const suggestions = [];
  const fromMins = fromTime
    ? parseInt(fromTime.split(':')[0], 10) * 60 + parseInt(fromTime.split(':')[1], 10)
    : -1;

  // Same-day: only slots strictly after the taken time.
  const sameDay = computeSlotsForDate(fromDate) || [];
  for (const slot of sameDay) {
    if (suggestions.length >= count) break;
    if (slot.status !== 'available') continue;
    const [h, m] = slot.time.split(':').map(Number);
    const mins = h * 60 + m;
    if (fromMins >= 0 && mins <= fromMins) continue;
    suggestions.push({ date: new Date(fromDate), time: slot.time, sameDay: true });
  }

  if (suggestions.length >= count) return suggestions;

  // Scan forward, skipping disabled days.
  for (let d = 1; d <= lookAheadDays && suggestions.length < count; d += 1) {
    const next = new Date(fromDate);
    next.setDate(next.getDate() + d);
    if (typeof isDateDisabled === 'function' && isDateDisabled(next)) continue;
    const daySlots = computeSlotsForDate(next) || [];
    for (const slot of daySlots) {
      if (suggestions.length >= count) break;
      if (slot.status !== 'available') continue;
      suggestions.push({ date: new Date(next), time: slot.time, sameDay: false });
    }
  }

  return suggestions;
}
